import { useState, useEffect } from 'react';
import {
  Globe,
  Palette,
  Save,
  RefreshCw,
  Eye,
  ExternalLink,
  Check,
  AlertCircle,
  Upload,
  Trash2,
  Code,
  Languages,
  Settings,
  MessageSquare,
  Ticket,
  Search,
} from 'lucide-react';
import { Button, Card, Spinner, Badge, useToast } from '@centraoneplus/ui';
import {
  portalConfigService,
  type PortalConfig,
  type CreatePortalConfigDto,
} from '@services/portal-config.service';
import { ThemedSelect } from '@components/common/ThemedSelect';

const defaultTheme = {
  primaryColor: '#3B82F6',
  secondaryColor: '#6366F1',
  accentColor: '#8B5CF6',
  backgroundColor: '#FFFFFF',
  textColor: '#1F2937',
  headerColor: '#1F2937',
  footerColor: '#F3F4F6',
};

const languageOptions = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ja', label: 'Japanese' },
  { code: 'zh', label: 'Chinese' },
];

export default function PortalSettings() {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [config, setConfig] = useState<PortalConfig | null>(null);
  const [formData, setFormData] = useState<CreatePortalConfigDto>({
    name: '',
    subdomain: '',
    customDomain: '',
    logoUrl: '',
    faviconUrl: '',
    theme: defaultTheme,
    enableDarkMode: true,
    enableSearch: true,
    enableContactForm: true,
    enableChat: false,
    enableTicketSubmission: true,
    enableTicketTracking: true,
    welcomeMessage: '',
    footerText: '',
    customCss: '',
    metaTitle: '',
    metaDescription: '',
    googleAnalyticsId: '',
    languages: ['en'],
    defaultLanguage: 'en',
    metadata: { socialMedia: { twitter: '', linkedin: '', facebook: '', instagram: '', youtube: '' } },
  });
  const [activeTab, setActiveTab] = useState<'general' | 'branding' | 'features' | 'advanced'>('general');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setIsLoading(true);
    try {
      const result = await portalConfigService.get();
      if (result) {
        setConfig(result);
        setFormData({
          name: result.name || '',
          subdomain: result.subdomain || '',
          customDomain: result.customDomain || '',
          logoUrl: result.logoUrl || '',
          faviconUrl: result.faviconUrl || '',
          theme: result.theme || defaultTheme,
          enableDarkMode: result.enableDarkMode,
          enableSearch: result.enableSearch,
          enableContactForm: result.enableContactForm,
          enableChat: result.enableChat,
          enableTicketSubmission: result.enableTicketSubmission,
          enableTicketTracking: result.enableTicketTracking,
          welcomeMessage: result.welcomeMessage || '',
          footerText: result.footerText || '',
          customCss: result.customCss || '',
          metaTitle: result.metaTitle || '',
          metaDescription: result.metaDescription || '',
          googleAnalyticsId: result.googleAnalyticsId || '',
          languages: result.languages || ['en'],
          defaultLanguage: result.defaultLanguage || 'en',
          metadata: result.metadata || { socialMedia: { twitter: '', linkedin: '', facebook: '', instagram: '', youtube: '' } },
        });
      }
    } catch (err) {
      console.error('Failed to fetch portal config:', err);
      toast.error(
        'Failed to load portal settings',
        (err as any)?.response?.data?.message ||
          (err as any)?.response?.data?.error?.message ||
          (err as any)?.message ||
          'Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (config) {
        await portalConfigService.update(formData);
        toast.success('Saved', 'Portal settings updated successfully');
      } else {
        const newConfig = await portalConfigService.create(formData);
        setConfig(newConfig);
        toast.success('Created', 'Portal configuration created successfully');
      }
      fetchConfig();
    } catch (err) {
      console.error('Failed to save portal config:', err);
      toast.error('Error', 'Failed to save portal settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete the portal configuration? This cannot be undone.')) {
      return;
    }

    try {
      await portalConfigService.delete();
      setConfig(null);
      setFormData({
        ...formData,
        name: '',
        subdomain: '',
        customDomain: '',
      });
      toast.success('Deleted', 'Portal configuration deleted');
    } catch (err) {
      console.error('Failed to delete portal config:', err);
      toast.error('Error', 'Failed to delete portal configuration');
    }
  };

  const updateTheme = (key: string, value: string) => {
    setFormData({
      ...formData,
      theme: {
        ...formData.theme,
        [key]: value,
      },
    });
  };

  const toggleLanguage = (code: string) => {
    const languages = formData.languages || [];
    if (languages.includes(code)) {
      if (languages.length > 1) {
        setFormData({
          ...formData,
          languages: languages.filter((l) => l !== code),
          defaultLanguage: formData.defaultLanguage === code ? languages[0] : formData.defaultLanguage,
        });
      }
    } else {
      setFormData({
        ...formData,
        languages: [...languages, code],
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Customer Portal</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configure your self-service customer portal
          </p>
        </div>
        <div className="flex items-center gap-2">
          {config && (
            <a
              href={portalConfigService.getPreviewUrl(config)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              Preview
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Status */}
      {config && (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${config.isActive ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100'}`}>
                <Globe className={`h-5 w-5 ${config.isActive ? 'text-green-600' : 'text-gray-400'} dark:text-gray-500`} />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {config.name || 'Customer Portal'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {config.subdomain ? `${config.subdomain}.portal.centraoneplus.com` : 'No subdomain configured'}
                </p>
              </div>
            </div>
            <Badge variant={config.isActive ? 'success' : 'default'}>
              {config.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          {[
            { id: 'general', label: 'General', icon: Settings },
            { id: 'branding', label: 'Branding', icon: Palette },
            { id: 'features', label: 'Features', icon: Ticket },
            { id: 'advanced', label: 'Advanced', icon: Code },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <Card className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Portal Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                placeholder="Help Center"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subdomain
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={formData.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  className="input rounded-r-none flex-1"
                  placeholder="mycompany"
                />
                <span className="inline-flex items-center px-3 bg-gray-100 dark:bg-gray-700 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-lg text-sm text-gray-500 dark:text-gray-400">
                  .portal.centraoneplus.com
                </span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Custom Domain (Optional)
              </label>
              <input
                type="text"
                value={formData.customDomain}
                onChange={(e) => setFormData({ ...formData, customDomain: e.target.value })}
                className="input w-full"
                placeholder="help.yourcompany.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Default Language
              </label>
              <ThemedSelect
                value={formData.defaultLanguage}
                onChange={(e) => setFormData({ ...formData, defaultLanguage: e.target.value })}
                className="input w-full"
              >
                {languageOptions.map((lang) => (
                  <option key={lang.code} value={lang.code} disabled={!formData.languages?.includes(lang.code)}>
                    {lang.label}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Welcome Message
            </label>
            <textarea
              value={formData.welcomeMessage}
              onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
              className="input w-full"
              rows={3}
              placeholder="Welcome to our Help Center! How can we assist you today?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Supported Languages
            </label>
            <div className="flex flex-wrap gap-2">
              {languageOptions.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => toggleLanguage(lang.code)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    formData.languages?.includes(lang.code)
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200'
                  }`}
                >
                  {formData.languages?.includes(lang.code) && <Check className="h-3 w-3 inline mr-1" />}
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* Social Media Links */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Social Media Links
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Add your social media URLs. These will appear on the Contact Us page.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'twitter', label: 'Twitter / X', placeholder: 'https://twitter.com/yourcompany' },
                { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/yourcompany' },
                { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/yourcompany' },
                { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourcompany' },
                { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@yourcompany' },
              ].map((social) => (
                <div key={social.key}>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {social.label}
                  </label>
                  <input
                    type="url"
                    value={formData.metadata?.socialMedia?.[social.key] || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        metadata: {
                          ...formData.metadata,
                          socialMedia: {
                            ...formData.metadata?.socialMedia,
                            [social.key]: e.target.value,
                          },
                        },
                      })
                    }
                    className="input w-full text-sm"
                    placeholder={social.placeholder}
                  />
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Branding Tab */}
      {activeTab === 'branding' && (
        <Card className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Logo URL
              </label>
              <input
                type="url"
                value={formData.logoUrl}
                onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                className="input w-full"
                placeholder="https://example.com/logo.png"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Favicon URL
              </label>
              <input
                type="url"
                value={formData.faviconUrl}
                onChange={(e) => setFormData({ ...formData, faviconUrl: e.target.value })}
                className="input w-full"
                placeholder="https://example.com/favicon.ico"
              />
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Theme Colors</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(formData.theme || defaultTheme).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={value}
                      onChange={(e) => updateTheme(key, e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => updateTheme(key, e.target.value)}
                      className="input flex-1 text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Dark Mode</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Allow users to toggle dark mode
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enableDarkMode}
                onChange={(e) => setFormData({ ...formData, enableDarkMode: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Footer Text
            </label>
            <input
              type="text"
              value={formData.footerText}
              onChange={(e) => setFormData({ ...formData, footerText: e.target.value })}
              className="input w-full"
              placeholder="© 2025 Your Company. All rights reserved."
            />
          </div>
        </Card>
      )}

      {/* Features Tab */}
      {activeTab === 'features' && (
        <Card className="p-6 space-y-4">
          {[
            { key: 'enableSearch', label: 'Search', description: 'Allow users to search the knowledge base', icon: Search },
            { key: 'enableContactForm', label: 'Contact Form', description: 'Show a contact form for inquiries', icon: MessageSquare },
            { key: 'enableChat', label: 'Live Chat', description: 'Enable live chat widget', icon: MessageSquare },
            { key: 'enableTicketSubmission', label: 'Ticket Submission', description: 'Allow users to submit support tickets', icon: Ticket },
            { key: 'enableTicketTracking', label: 'Ticket Tracking', description: 'Allow users to track their tickets', icon: Ticket },
          ].map((feature) => (
            <div key={feature.key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white dark:bg-gray-700 rounded-lg">
                  <feature.icon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{feature.label}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{feature.description}</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData[feature.key as keyof typeof formData] as boolean}
                  onChange={(e) => setFormData({ ...formData, [feature.key]: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
              </label>
            </div>
          ))}
        </Card>
      )}

      {/* Advanced Tab */}
      {activeTab === 'advanced' && (
        <Card className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Meta Title
              </label>
              <input
                type="text"
                value={formData.metaTitle}
                onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
                className="input w-full"
                placeholder="Help Center | Your Company"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Google Analytics ID
              </label>
              <input
                type="text"
                value={formData.googleAnalyticsId}
                onChange={(e) => setFormData({ ...formData, googleAnalyticsId: e.target.value })}
                className="input w-full"
                placeholder="G-XXXXXXXXXX"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Meta Description
            </label>
            <textarea
              value={formData.metaDescription}
              onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
              className="input w-full"
              rows={2}
              placeholder="Find answers to your questions in our comprehensive help center."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Custom CSS
            </label>
            <textarea
              value={formData.customCss}
              onChange={(e) => setFormData({ ...formData, customCss: e.target.value })}
              className="input w-full font-mono text-sm"
              rows={6}
              placeholder="/* Add your custom CSS here */"
            />
          </div>

          {config && (
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">Danger Zone</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Permanently delete this portal configuration. This action cannot be undone.
              </p>
              <Button variant="outline" onClick={handleDelete} className="text-red-600 border-red-600 hover:bg-red-50">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Portal Configuration
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
